/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=2226-105987&m=dev
 *
 * Manage order section — customers always see the Return button, disabled
 * when the order is not eligible. Hidden entirely when no actions are
 * available (e.g. a style advisor on an order with nothing to cancel).
 */

import { Button, Checkbox, ListItem, Modal } from '@susu/ui/components'
import { useMemo, useState } from 'react'

import type { Environment } from '../../../App'
import { useLabel } from '../../../i18n'
import ProductCard from '../cards/ProductCard'
import type { OrderItem } from '../types'

interface ManageOrderProps {
  isReturnEligible?: boolean
  items: OrderItem[]
  env: Environment
  role?: 'customer' | 'style-advisor'
  isCancellationFlowEnabled?: boolean
  onReturn?: () => void
  onItemDetailsClick?: (item: OrderItem) => void
  onReturnDetailsClick?: (item: OrderItem) => void
  onCancelContinue?: (items: OrderItem[]) => Promise<void> | void
  onCancellationClicked?: () => void
}

export default function ManageOrder({
  isReturnEligible = false,
  items,
  env,
  role = 'customer',
  isCancellationFlowEnabled = true,
  onReturn,
  onItemDetailsClick,
  onReturnDetailsClick,
  onCancelContinue,
  onCancellationClicked,
}: ManageOrderProps) {
  const t = useLabel()
  const [cancellationStep, setCancellationStep] = useState<
    'select' | 'confirm'
  >('select')
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [selectedItemIds, setSelectedItemIds] = useState<
    Record<string, boolean>
  >({})
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const cancellableItems = useMemo(
    () => items.filter((item) => item.isCancellable),
    [items],
  )
  const hasSelectedItems = useMemo(
    () => cancellableItems.some((item) => selectedItemIds[item.id]),
    [cancellableItems, selectedItemIds],
  )
  const isOrderCancellable =
    isCancellationFlowEnabled && items.some((item) => item.isCancellable)

  const showReturn = role === 'customer'

  if (!showReturn && !isOrderCancellable) {
    return null
  }

  const handleReturn = () => {
    onReturn?.()
  }

  const handleCancel = () => {
    onCancellationClicked?.()
    setSelectedItemIds({})
    setCancelError(null)
    setCancellationStep('select')
    setIsCancelModalOpen(true)
  }

  const handleCancelContinue = () => {
    setCancelError(null)
    setCancellationStep('confirm')
  }

  const handleConfirmCancellation = async () => {
    const selectedItems = cancellableItems.filter(
      (item) => selectedItemIds[item.id],
    )

    if (!onCancelContinue) {
      setIsCancelModalOpen(false)
      return
    }

    try {
      setIsCancelling(true)
      setCancelError(null)
      await onCancelContinue(selectedItems)
      setIsCancelModalOpen(false)
    } catch {
      setCancelError(t('orderList.manageOrder.cancelFailed'))
    } finally {
      setIsCancelling(false)
    }
  }

  return (
    <>
      <div className="flex flex-col gap-16">
        <h2 className="text-xl leading-xl lg:text-2xl lg:leading-2xl font-medium text-text tracking-heading">
          {t('orderList.manageOrder.title')}
        </h2>
        <div className="flex flex-col gap-12">
          {showReturn && (
            <ListItem
              variant="bordered"
              leadingIcon="return-arrow"
              title={t('orderList.manageOrder.return')}
              onClick={handleReturn}
              disabled={!isReturnEligible}
              showTrailingIcon={false}
              className="rounded-lg p-20"
            />
          )}
          {isOrderCancellable && (
            <ListItem
              variant="bordered"
              leadingIcon="none"
              title={t('orderList.manageOrder.cancelOrder')}
              onClick={handleCancel}
              showTrailingIcon={false}
              className="rounded-lg p-20"
            />
          )}
        </div>
      </div>
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false)
          setCancellationStep('select')
          setCancelError(null)
        }}
        showHeaderBorder={false}
      >
        {cancellationStep === 'select' ? (
          <div className="flex h-full min-h-0 flex-col">
            <div className="lg:px-20 -mt-2 flex shrink-0 items-center justify-center px-16 3xl:px-120">
              <h1 className="text-2xl leading-2xl font-medium -tracking-[0.015em] text-text">
                {t('orderList.manageOrder.selectItems')}
              </h1>
            </div>
            <div className="flex flex-1 min-h-0 flex-col px-16 pt-32">
              <div className="flex flex-1 min-h-0 flex-col gap-16 overflow-y-auto">
                {cancellableItems.map((item) => (
                  <div
                    key={item.id}
                    className={`relative rounded-lg border bg-surface p-16 ${
                      selectedItemIds[item.id]
                        ? 'border-border-selected'
                        : 'border-border'
                    }`}
                  >
                    <div
                      className="absolute right-16 top-16 z-10 flex items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        aria-label={t(
                          'orderList.manageOrder.selectItemForCancellation',
                          {
                            itemName:
                              item.name ??
                              t('orderList.manageOrder.unnamedItem'),
                          },
                        )}
                        checked={!!selectedItemIds[item.id]}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setCancelError(null)
                          setSelectedItemIds((prev) => ({
                            ...prev,
                            [item.id]: checked,
                          }))
                        }}
                      />
                    </div>
                    <ProductCard
                      item={item}
                      env={env}
                      role={role}
                      onItemDetailsClick={onItemDetailsClick}
                      onReturnDetailsClick={onReturnDetailsClick}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="shrink-0 border-t border-border px-8 py-16">
              <Button
                variant="filled"
                size="m"
                className="w-full"
                onClick={handleCancelContinue}
                disabled={!hasSelectedItems}
              >
                {t('orderList.manageOrder.continue')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col px-16 py-24">
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <h1 className="text-2xl leading-2xl font-medium text-text">
                {t('orderList.manageOrder.confirmCancellation')}
              </h1>
              <p className="mt-12 text-base leading-base font-light text-text">
                {t('orderList.manageOrder.cancellationWarning')}
              </p>
              {cancelError && (
                <div className="mt-12 text-center text-sm leading-sm font-light text-text-warning">
                  {cancelError}
                </div>
              )}
              <div className="mt-32 flex flex-col items-center gap-10">
                <Button
                  variant="filled"
                  size="m"
                  className="w-[220px]"
                  onClick={handleConfirmCancellation}
                  loading={isCancelling}
                >
                  {t('orderList.manageOrder.confirmCancellation')}
                </Button>
                <Button
                  variant="secondary"
                  size="m"
                  className="w-[220px]"
                  onClick={() => setIsCancelModalOpen(false)}
                  disabled={isCancelling}
                >
                  {t('orderList.manageOrder.keepItems')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
